import React from 'react'

import DateInput from '../../components/DateInput'
import { useLocale, useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { Lot } from '../../types/lot'
import {
  diameterFromVonBayer,
  eggsFromVolume,
  readEggArrival,
  readStrain,
  readSupplier,
  STOCK_TRAITS,
  type EggArrival,
  type StockTrait
} from '../../utils/hatchery'

// Hatchery v0 (findings #84) egg arrival record — the fields, their parsing and
// checks. Shared by Lotes → Nuevo lote → Ovas and by the lot's Incubación /
// "¿Este lote llegó como ovas?" editor (Office UX v0, findings #95), so both
// save exactly the same record (GLOBALG.A.P. 20.01.05 / 20.02.05,
// BAP Hatchery T4 / 4.7, ASC Trout P7).

export const TRAIT_KEY: Record<StockTrait, TranslationKey> = {
  allFemale: 'lots.hatchery.trait.allFemale',
  triploid: 'lots.hatchery.trait.triploid'
}

export const arrivalInputStyle: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, width: '100%', boxSizing: 'border-box' }

export type ArrivalDraft = {
  arrivalDate: string
  eggsReceived: string
  tuaAtArrival: string
  hatchTargetTua: string
  firstFeedingTargetTua: string
  eggDiameterMm: string
  vonBayerCount: string
  eggsPerGram: string
  eggsPerLitre: string
  eggVolumeLitres: string
  supplier: string
  supplierLot: string
  supplierCertification: string
  strain: string
  broodstockGroup: string
  spawningDate: string
  stockTraits: StockTrait[]
  healthCertificateNo: string
  healthCertificateDate: string
  quarantine: boolean
  disinfectionProduct: string
  disinfectionPpm: string
  disinfectionMinutes: string
  disinfectionDate: string
}

const str = (value: number | string | undefined | null): string => (value === undefined || value === null ? '' : String(value))

export const emptyArrivalDraft = (arrivalDate: string): ArrivalDraft => ({
  arrivalDate,
  eggsReceived: '',
  tuaAtArrival: '',
  hatchTargetTua: '',
  firstFeedingTargetTua: '',
  eggDiameterMm: '',
  vonBayerCount: '',
  eggsPerGram: '',
  eggsPerLitre: '',
  eggVolumeLitres: '',
  supplier: '',
  supplierLot: '',
  supplierCertification: '',
  strain: '',
  broodstockGroup: '',
  spawningDate: '',
  stockTraits: [],
  healthCertificateNo: '',
  healthCertificateDate: '',
  quarantine: false,
  disinfectionProduct: '',
  disinfectionPpm: '',
  disinfectionMinutes: '',
  disinfectionDate: ''
})

export const toArrivalDraft = (lot: Lot, source: Lot, today: string): ArrivalDraft => {
  const arrival = readEggArrival(source)
  return {
    arrivalDate: lot.stockingDate ? String(lot.stockingDate).slice(0, 10) : today,
    eggsReceived: readEggArrival(lot).countFromVolume
      ? ''
      : str(readEggArrival(lot).eggsReceived ?? (lot.metadata?.stockedFishCount as number | undefined)),
    tuaAtArrival: str(arrival.tuaAtArrival),
    hatchTargetTua: str(arrival.hatchTargetTua),
    firstFeedingTargetTua: str(arrival.firstFeedingTargetTua),
    eggDiameterMm: str(arrival.eggDiameterMm),
    vonBayerCount: str(arrival.vonBayerCount),
    eggsPerGram: str(arrival.eggsPerGram),
    eggsPerLitre: str(arrival.eggsPerLitre),
    eggVolumeLitres: str(readEggArrival(lot).eggVolumeLitres),
    supplier: readSupplier(source) ?? '',
    supplierLot: arrival.supplierLot ?? '',
    supplierCertification: arrival.supplierCertification ?? '',
    strain: readStrain(source) ?? '',
    broodstockGroup: arrival.broodstockGroup ?? '',
    spawningDate: arrival.spawningDate ?? '',
    stockTraits: arrival.stockTraits ?? [],
    healthCertificateNo: arrival.healthCertificateNo ?? '',
    healthCertificateDate: arrival.healthCertificateDate ?? '',
    quarantine: arrival.quarantine ?? false,
    disinfectionProduct: arrival.disinfectionProduct ?? '',
    disinfectionPpm: str(arrival.disinfectionPpm),
    disinfectionMinutes: str(arrival.disinfectionMinutes),
    disinfectionDate: arrival.disinfectionDate ?? ''
  }
}

const parseOptional = (value: string, options: { integer?: boolean; allowZero?: boolean } = {}): number | undefined | 'invalid' => {
  const trimmed = value.trim().replace(/\s/g, '').replace(',', '.')
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < 0 || (!options.allowZero && parsed === 0)) return 'invalid'
  if (options.integer && !Number.isInteger(parsed)) return 'invalid'
  return parsed
}

export type ParsedArrival = {
  arrivalDate: string
  /** Fields shared by every lot of the same egg batch. */
  shared: EggArrival
  volumeLitres: number | undefined
  typedCount: number | undefined
  /** Eggs per litre × litres when no exact count was typed (Von Bayer, BAP Hatchery 2.1 3.50). */
  estimated: number | null
}

export const parseArrivalDraft = (draft: ArrivalDraft): { ok: true; value: ParsedArrival } | { ok: false; errorKey: TranslationKey } => {
  const numbers = {
    eggsReceived: parseOptional(draft.eggsReceived, { integer: true }),
    tuaAtArrival: parseOptional(draft.tuaAtArrival, { allowZero: true }),
    hatchTargetTua: parseOptional(draft.hatchTargetTua),
    firstFeedingTargetTua: parseOptional(draft.firstFeedingTargetTua),
    eggDiameterMm: parseOptional(draft.eggDiameterMm),
    vonBayerCount: parseOptional(draft.vonBayerCount),
    eggsPerGram: parseOptional(draft.eggsPerGram),
    eggsPerLitre: parseOptional(draft.eggsPerLitre),
    eggVolumeLitres: parseOptional(draft.eggVolumeLitres),
    disinfectionPpm: parseOptional(draft.disinfectionPpm),
    disinfectionMinutes: parseOptional(draft.disinfectionMinutes)
  }
  if (Object.values(numbers).some((value) => value === 'invalid')) {
    return { ok: false, errorKey: 'lots.hatchery.error.invalidNumber' }
  }
  if (typeof numbers.eggDiameterMm === 'number' && numbers.eggDiameterMm > 15) {
    return { ok: false, errorKey: 'lots.hatchery.error.diameter' }
  }
  if (typeof numbers.hatchTargetTua === 'number' && typeof numbers.firstFeedingTargetTua === 'number'
    && numbers.firstFeedingTargetTua <= numbers.hatchTargetTua) {
    return { ok: false, errorKey: 'lots.hatchery.error.targetsOrder' }
  }
  if (!draft.arrivalDate) {
    return { ok: false, errorKey: 'lots.hatchery.error.arrivalDate' }
  }
  const shared: EggArrival = {
    tuaAtArrival: numbers.tuaAtArrival as number | undefined,
    hatchTargetTua: numbers.hatchTargetTua as number | undefined,
    firstFeedingTargetTua: numbers.firstFeedingTargetTua as number | undefined,
    // findings #112: a typed diameter wins; otherwise it comes from the Von Bayer count.
    eggDiameterMm: (numbers.eggDiameterMm as number | undefined) ?? diameterFromVonBayer(numbers.vonBayerCount as number | undefined) ?? undefined,
    vonBayerCount: numbers.vonBayerCount as number | undefined,
    eggsPerGram: numbers.eggsPerGram as number | undefined,
    eggsPerLitre: numbers.eggsPerLitre as number | undefined,
    supplierLot: draft.supplierLot.trim() || undefined,
    supplierCertification: draft.supplierCertification.trim() || undefined,
    broodstockGroup: draft.broodstockGroup.trim() || undefined,
    spawningDate: draft.spawningDate || undefined,
    stockTraits: draft.stockTraits,
    healthCertificateNo: draft.healthCertificateNo.trim() || undefined,
    healthCertificateDate: draft.healthCertificateDate || undefined,
    quarantine: draft.quarantine,
    disinfectionProduct: draft.disinfectionProduct.trim() || undefined,
    disinfectionPpm: numbers.disinfectionPpm as number | undefined,
    disinfectionMinutes: numbers.disinfectionMinutes as number | undefined,
    disinfectionDate: draft.disinfectionDate || undefined
  }
  const volumeLitres = numbers.eggVolumeLitres as number | undefined
  const typedCount = numbers.eggsReceived as number | undefined
  const estimated = typedCount === undefined
    ? eggsFromVolume({ eggsPerLitre: shared.eggsPerLitre, eggVolumeLitres: volumeLitres })
    : null
  return { ok: true, value: { arrivalDate: draft.arrivalDate, shared, volumeLitres, typedCount, estimated } }
}

/** The arrival as stored on the lot the office typed it for (its own count / volume). */
export const ownArrival = (parsed: ParsedArrival): EggArrival => ({
  ...parsed.shared,
  eggVolumeLitres: parsed.volumeLitres,
  eggsReceived: parsed.typedCount ?? parsed.estimated ?? undefined,
  countFromVolume: parsed.estimated !== null ? true : undefined
})

export type EggArrivalFieldsProps = {
  draft: ArrivalDraft
  onChange: (update: (current: ArrivalDraft) => ArrivalDraft) => void
  /** Hide the arrival date when the surrounding form already asks for it. */
  showArrivalDate?: boolean
  /** Hide the count fields when the surrounding form asks eggs per unit (Sembrar en varias unidades, #109). */
  showCount?: boolean
}

export function EggArrivalFields({ draft, onChange, showArrivalDate = true, showCount = true }: EggArrivalFieldsProps) {
  const t = useT()
  const locale = useLocale()

  const field = (key: keyof ArrivalDraft, labelKey: TranslationKey, options: { decimal?: boolean; testId?: string } = {}) => (
    <label style={{ display: 'grid', gap: 2, fontSize: 12, color: '#334155' }}>
      <span>{t(labelKey)}</span>
      <input
        value={String(draft[key] ?? '')}
        inputMode={options.decimal ? 'decimal' : undefined}
        onChange={(event) => {
          const value = event.target.value
          onChange((current) => ({ ...current, [key]: value }))
        }}
        style={arrivalInputStyle}
        data-testid={options.testId ?? `lot-hatchery-field-${key}`}
      />
    </label>
  )

  const dateField = (key: 'arrivalDate' | 'healthCertificateDate' | 'disinfectionDate' | 'spawningDate', labelKey: TranslationKey) => (
    <label style={{ display: 'grid', gap: 2, fontSize: 12, color: '#334155' }}>
      <span>{t(labelKey)}</span>
      <DateInput
        value={draft[key] ?? ''}
        onChangeValue={(nextValue) => onChange((current) => ({ ...current, [key]: nextValue }))}
        pickerOnly
        style={arrivalInputStyle}
      />
    </label>
  )

  return (
    <>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {showArrivalDate ? dateField('arrivalDate', 'lots.hatchery.field.arrivalDate') : null}
        {showCount ? field('eggsReceived', 'lots.hatchery.field.eggsReceived', { decimal: true }) : null}
        {field('tuaAtArrival', 'lots.hatchery.field.tuaAtArrival', { decimal: true })}
        {field('hatchTargetTua', 'lots.hatchery.field.hatchTargetTua', { decimal: true })}
        {field('firstFeedingTargetTua', 'lots.hatchery.field.firstFeedingTargetTua', { decimal: true })}
        {field('vonBayerCount', 'lots.hatchery.field.vonBayerCount', { decimal: true })}
        {field('eggDiameterMm', 'lots.hatchery.field.eggDiameterMm', { decimal: true })}
        {field('eggsPerGram', 'lots.hatchery.field.eggsPerGram', { decimal: true })}
        {showCount ? field('eggsPerLitre', 'lots.hatchery.field.eggsPerLitre', { decimal: true }) : null}
        {showCount ? field('eggVolumeLitres', 'lots.hatchery.field.eggVolumeLitres', { decimal: true }) : null}
      </div>
      {(() => {
        const count = Number(draft.vonBayerCount.trim().replace(',', '.'))
        const derived = !draft.eggDiameterMm.trim() ? diameterFromVonBayer(count) : null
        return derived !== null
          ? <span style={{ fontSize: 12, color: '#0369a1' }} data-testid="lot-hatchery-vonbayer-diameter">{t('lots.hatchery.arrival.vonBayerDiameter', { mm: derived.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}</span>
          : null
      })()}
      {showCount ? <span style={{ fontSize: 12, color: '#64748b' }}>{t('lots.hatchery.arrival.countHelp')}</span> : null}
      <strong style={{ fontSize: 12, color: '#0369a1' }}>{t('lots.hatchery.arrival.origin')}</strong>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {field('supplier', 'lots.hatchery.field.supplier')}
        {field('supplierLot', 'lots.hatchery.field.supplierLot')}
        {field('supplierCertification', 'lots.hatchery.field.supplierCertification')}
        {field('strain', 'lots.hatchery.field.strain')}
        {field('broodstockGroup', 'lots.hatchery.field.broodstockGroup')}
        {dateField('spawningDate', 'lots.hatchery.field.spawningDate')}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12 }}>
        {STOCK_TRAITS.map((trait) => (
          <label key={trait} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={draft.stockTraits.includes(trait)}
              onChange={(event) => {
                const checked = event.target.checked
                onChange((current) => ({
                  ...current,
                  stockTraits: checked
                    ? [...current.stockTraits, trait]
                    : current.stockTraits.filter((entry) => entry !== trait)
                }))
              }}
              data-testid={`lot-hatchery-trait-${trait}`}
            />
            {t(TRAIT_KEY[trait])}
          </label>
        ))}
      </div>
      <strong style={{ fontSize: 12, color: '#0369a1' }}>{t('lots.hatchery.arrival.health')}</strong>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {field('healthCertificateNo', 'lots.hatchery.field.healthCertificateNo')}
        {dateField('healthCertificateDate', 'lots.hatchery.field.healthCertificateDate')}
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#334155' }}>
          <input
            type="checkbox"
            checked={draft.quarantine}
            onChange={(event) => {
              const checked = event.target.checked
              onChange((current) => ({ ...current, quarantine: checked }))
            }}
            data-testid="lot-hatchery-quarantine"
          />
          {t('lots.hatchery.field.quarantine')}
        </label>
      </div>
      <strong style={{ fontSize: 12, color: '#0369a1' }}>{t('lots.hatchery.arrival.disinfection')}</strong>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        {field('disinfectionProduct', 'lots.hatchery.field.disinfectionProduct')}
        {field('disinfectionPpm', 'lots.hatchery.field.disinfectionPpm', { decimal: true })}
        {field('disinfectionMinutes', 'lots.hatchery.field.disinfectionMinutes', { decimal: true })}
        {dateField('disinfectionDate', 'lots.hatchery.field.disinfectionDate')}
      </div>
      <span style={{ fontSize: 12, color: '#64748b' }}>{t('lots.hatchery.arrival.help')}</span>
    </>
  )
}
